import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Breadcrumbs,
  Link,
  CircularProgress,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import "./stateDetails.css";

// Register the necessary chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const dummyPieData = {
  labels: ["Data Incoming"],
  datasets: [
    {
      data: [100],
      backgroundColor: ["#cccccc"],
      hoverBackgroundColor: ["#cccccc"],
    },
  ],
};

const dummyOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  tooltips: {
    enabled: false,
  },
  hover: {
    mode: null,
  },
};

const StateDetail = () => {
  const { stateName: paramStateName } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const stateName = React.useMemo(() => {
    if (paramStateName) {
      sessionStorage.setItem("selectedState", paramStateName); // Save to sessionStorage
      return paramStateName;
    }
    const storedState = sessionStorage.getItem("selectedState"); // Retrieve from sessionStorage
    if (storedState) {
      return storedState;
    }
    return "";
  }, [paramStateName]);
  // Add the POST request useEffect here
  useEffect(() => {
    if (stateName) {
      const stateNamePayload = {
        circle: stateName,
      };
      console.log(stateNamePayload);

      const sendStateName = async () => {
        try {
          const response = await fetch(
            "https://api.bharatnet.sunshineiotronics.com/api/v2/analysis/sites",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(stateNamePayload),
            }
          );

          if (!response.ok) {
            throw new Error("Failed to send state name");
          }

          const responseData = await response.json();
          setData(responseData);
          console.log(responseData);
          setTimeout(() => {
            setLoading(false);
          }, 500);
        } catch (error) {
          console.error("Error sending state name:", error);
          setTimeout(() => {
            setLoading(false);
          }, 500);
        }
      };

      sendStateName();
    }
  }, [stateName]);
  useEffect(() => {
    const userData = sessionStorage.getItem("userData");
    if (!userData) {
      navigate("/");
    } else {
      sessionStorage.setItem("lastVisitedPage", `/${stateName}/displaySite`);
    }
  }, [navigate]);
  const breadcrumbs = [
    <Link key="1" href="/dashboard" sx={{ color: "black" }}>
      Dashboard
    </Link>,
    <Typography key="3" sx={{ color: "text.primary" }}>
      {stateName}
    </Typography>,
  ];

  // Pie chart data
  const totalSites = data?.total_sites || 0;
  const liveSites = data?.live_sites || 0;
  const criticalSites = data?.critical_sites || 0;
  const majorSites = data?.major_sites || 0;
  const minorSites = data?.minor_sites || 0;
  const allOkSites = data?.all_ok_sites;
  const pieData = {
    labels: ["All OK", "Minor", "Major", "Critical"],
    datasets: [
      {
        data: [allOkSites, minorSites, majorSites, criticalSites],
        backgroundColor: [
          "#4caf50", // All OK
          "#ff9800", // Minor
          "#f44336", // Major
          "#9c27b0", // Critical
        ],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false, // Disable default legend
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            let label = tooltipItem.label || "";
            label += ": " + tooltipItem.raw;
            return label;
          },
        },
      },
    },
    // Add onClick handler directly within the options
    onClick: (e) => {
      const chart = e.chart;
      const activePoints = chart.getElementsAtEventForMode(
        e.native,
        "nearest",
        { intersect: true },
        false
      );
      if (activePoints.length > 0) {
        // You can access the clicked segment data here
        const clickedIndex = activePoints[0].index;
        if (totalSites > 0) {
          navigate(`/${stateName}/displaySite`);
        }
      }
    },
  };

  // Dynamically create the custom legend
  const generateLegend = () => {
    return pieData.labels.map((label, index) => (
      <li key={index} className="chart-legend-item">
        <span
          className="legend-color-box"
          style={{
            backgroundColor: pieData.datasets[0].backgroundColor[index],
          }}
        ></span>
        {label}
      </li>
    ));
  };

  return (
    <Box m="1.5rem 2.5rem">
      <div className="container-fluid page-body-wrapper">
        <br />
        <br />
        <div className="main-panel">
          <div className="content-wrapper">
            <Stack spacing={2}>
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="medium" />}
                aria-label="breadcrumb"
              >
                {breadcrumbs}
              </Breadcrumbs>
            </Stack>
          </div>
        </div>
      </div>
      <br />
      <br />
      <Typography variant="h4">Details for {stateName}</Typography>
      <div id="stateInfo">
        <h1 id="stateTitle"></h1>
        <p id="stateDetails"></p>
      </div>

      <div id="siteStats">
        <div>
          <h3>Total Sites</h3>
          <p id="totalSites">{totalSites}</p>
        </div>
        <div>
          <h3>Live Sites</h3>
          <p id="liveSites">{liveSites}</p>
        </div>
      </div>

      {loading ? (
        <div id="pieChartContainer">
          <Pie data={dummyPieData} options={dummyOptions} id="dummyPieChart" />
        </div>
      ) : totalSites === 0 ? (
        <div className="no-sites-container">
          <div className="no-sites-content">
            <Typography variant="h5" className="no-sites-text">
              Oops! No Sites Available
            </Typography>
            <Typography variant="body1" className="no-sites-description">
              Looks like there are no sites available right now. Please check
              back later.
            </Typography>
          </div>
        </div>
      ) : (
        <div id="pieChartContainer">
          <Pie data={pieData} options={options} id="pieChart" />
        </div>
      )}
      <br />
      <br />

      {totalSites > 0 && (
        <ul className="chart-legend" id="chartLegend">
          {generateLegend()}
        </ul>
      )}
    </Box>
  );
};

export default StateDetail;
